import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";

type ThemeMode = "light" | "dark";

interface ThemeContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
  themeMode: ThemeMode;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const getInitialTheme = (): boolean => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("theme");
      if (savedTheme) {
        return savedTheme === "dark";
      }
      if (window.matchMedia) {
        try {
          const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
          return mediaQuery && mediaQuery.matches;
        } catch (error) {
          return false;
        }
      }
    }
    return false;
  };

  const [darkMode, setDarkMode] = useState<boolean>(getInitialTheme);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  useEffect(() => {
    localStorage.setItem("theme", darkMode ? "dark" : "light");

    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    // Kit contract for the --nu-* design tokens (#601 S1): the NU DATA
    // brand layer themes via <html data-theme="light|dark">. Stamp it in
    // lockstep with the existing Tailwind `dark` class — same mechanism,
    // two consumers.
    document.documentElement.setAttribute(
      "data-theme",
      darkMode ? "dark" : "light",
    );
  }, [darkMode]);

  return (
    <ThemeContext.Provider
      value={{
        darkMode,
        toggleDarkMode,
        themeMode: darkMode ? "dark" : "light",
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
